import { useState } from 'react';
import { isAxiosError } from 'axios';
import { updateProfilePassword } from '@/api/profile';
import { Icon } from '@/components/Icon';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';

/**
 * 1:1 Password::min(8)->mixedCase()->numbers()->symbols() на бэке
 * (ProfileController::updatePassword() в Laravel) — проверка только на то,
 * что реально можно проверить на фронте без сети; uncompromised() (утечки)
 * бэк всё равно проверит сам, эту часть здесь не дублируем.
 */
function passwordHint(password: string): string | null {
  if (password.length < 8) return 'Пароль должен быть не короче 8 символов.';
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
    return 'Пароль должен содержать заглавные и строчные буквы.';
  }
  if (!/\d/.test(password)) return 'Пароль должен содержать цифры.';
  if (!/[^a-zA-Z0-9]/.test(password)) return 'Пароль должен содержать спецсимволы.';
  return null;
}

/**
 * Смена пароля — отдельная, всегда редактируемая карточка (1:1 старый Orchid
 * ProfilePasswordLayout: свой Save, независимый от карточки "Информация
 * профиля"). login — только для отображения ("Единый логин", тот же
 * disabled-field, что был в старом экране).
 *
 * Живая валидация — под каждым полем, красным, но только после того, как
 * поле уже потеряло фокус (touched), а не с первой буквы: показывать ошибку,
 * пока пользователь ещё не закончил печатать, было бы просто шумом. Кнопка
 * "Обновить пароль" — финальная подстраховка тем же диалогом ("Понятно"),
 * что и ошибка сохранения (сети/сервера) — на случай, если до неё дошли, не
 * тронув оба поля (например вставили пароль и сразу нажали).
 */
export function ProfilePasswordCard({ login }: { login: string }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmationTouched, setConfirmationTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { alert: alertDialog, dialog } = useConfirmDialog();

  const passwordError = passwordHint(password);
  const mismatchError = confirmation && password !== confirmation ? 'Пароли не совпадают.' : null;

  const save = async () => {
    setPasswordTouched(true);
    setConfirmationTouched(true);
    if (passwordError) {
      await alertDialog(passwordError);
      return;
    }
    if (mismatchError) {
      await alertDialog(mismatchError);
      return;
    }
    setSaving(true);
    try {
      await updateProfilePassword({ password, passwordConfirmation: confirmation });
      setPassword('');
      setConfirmation('');
      setPasswordTouched(false);
      setConfirmationTouched(false);
      showToast('Пароль изменён');
    } catch (err) {
      const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      await alertDialog(message || 'Не удалось изменить пароль.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2 className="detail__subtitle">Смена пароля</h2>
      <div className="edit-fields">
        <label className="edit-field edit-field--readonly edit-field--full">
          <span>Единый логин (на сервере авторизации)</span>
          <input type="text" value={login || '–'} disabled readOnly />
        </label>
        <label className="edit-field edit-field--full">
          <span>Новый пароль</span>
          <PasswordInput
            value={password}
            onChange={setPassword}
            onBlur={() => setPasswordTouched(true)}
          />
          {passwordTouched && passwordError ? (
            <span className="edit-field__hint edit-field__hint--error">{passwordError}</span>
          ) : (
            <span className="edit-field__hint">
              Минимум 8 символов: заглавные и строчные буквы, цифры и спецсимволы.
            </span>
          )}
        </label>
        <label className="edit-field edit-field--full">
          <span>Подтверждение пароля</span>
          <PasswordInput
            value={confirmation}
            onChange={setConfirmation}
            onBlur={() => setConfirmationTouched(true)}
          />
          {confirmationTouched && mismatchError && (
            <span className="edit-field__hint edit-field__hint--error">{mismatchError}</span>
          )}
        </label>
      </div>
      <div className="detail__header-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn--primary" onClick={save} disabled={saving || !password}>
          <Icon name="check" size={15} /> {saving ? 'Сохранение…' : 'Обновить пароль'}
        </button>
      </div>
      {dialog}
    </div>
  );
}

/** Поле пароля с переключателем видимости (глаз) — нет иконки "перечёркнутый глаз" в наборе, состояние отличаем цветом (aria-pressed). */
function PasswordInput({
  value,
  onChange,
  onBlur,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="password-input">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        autoComplete="new-password"
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <button
        type="button"
        className="password-input__toggle"
        aria-pressed={visible}
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        onClick={() => setVisible((v) => !v)}
      >
        <Icon name="eye" size={15} />
      </button>
    </div>
  );
}
